/**
 * Timeline records fetch hook
 *
 * Provides paginated daily records for timeline view using TanStack Query
 * with infinite scrolling support, proper caching, loading states, and error handling.
 */

import { useInfiniteQuery } from "@tanstack/react-query";
import {
  createApiClient,
  ApiClientError,
  type ListDailyRecordsResponse,
  type DailyRecordResponse,
} from "@floline/api-client";
import { API_URL } from "@/env";

/** Query key factory for timeline records */
export const timelineRecordKeys = {
  all: ["timeline-records"] as const,
  list: (startDate?: string, endDate?: string) =>
    [...timelineRecordKeys.all, { startDate, endDate }] as const,
};

/** API client configured for timeline requests */
const apiClient = createApiClient(
  API_URL
);

/** Number of records to load per page (2 weeks) */
const TIMELINE_PAGE_SIZE = 14;

/** Options for timeline records query */
interface UseTimelineRecordsOptions {
  /** Start date filter in YYYY-MM-DD format */
  startDate?: string;
  /** End date filter in YYYY-MM-DD format */
  endDate?: string;
}

/**
 * Fetch paginated daily records from the API
 *
 * @param options - Query options including pagination and date filters
 * @returns Paginated list of daily records
 */
async function fetchTimelineRecords(
  options: UseTimelineRecordsOptions & { offset: number }
): Promise<ListDailyRecordsResponse> {
  const response = await apiClient.listDailyRecords({
    limit: TIMELINE_PAGE_SIZE,
    offset: options.offset,
    startDate: options.startDate,
    endDate: options.endDate,
  });
  return response;
}

/**
 * Hook to fetch paginated daily records for timeline view
 *
 * Uses infinite query with pagination support. Returns pages of daily records
 * along with loading states, error handling, and functions to load more data.
 *
 * @param options - Optional date range filters
 *
 * @example
 * ```tsx
 * function TimelinePage() {
 *   const {
 *     pages,
 *     isLoading,
 *     hasNextPage,
 *     fetchNextPage,
 *     isFetchingNextPage
 *   } = useTimelineRecords({
 *     startDate: "2026-01-01",
 *     endDate: "2026-12-31"
 *   });
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return (
 *     <>
 *       {pages?.map(page =>
 *         page.data.map(record => <RecordCard key={record.date} {...record} />)
 *       )}
 *       {hasNextPage && (
 *         <button onClick={fetchNextPage} disabled={isFetchingNextPage}>
 *           Load More
 *         </button>
 *       )}
 *     </>
 *   );
 * }
 * ```
 */
export function useTimelineRecords(options: UseTimelineRecordsOptions = {}) {
  const query = useInfiniteQuery({
    queryKey: timelineRecordKeys.list(options.startDate, options.endDate),
    queryFn: ({ pageParam = 0 }) =>
      fetchTimelineRecords({
        ...options,
        offset: pageParam,
      }),
    getNextPageParam: (lastPage) => {
      const { offset, limit, total } = lastPage.meta;
      const nextOffset = offset + limit;
      return nextOffset < total ? nextOffset : undefined;
    },
    initialPageParam: 0,
    // Timeline data can be cached for a short time
    staleTime: 30 * 1000, // 30 seconds
    // Retry on errors (except auth errors)
    retry: (failureCount: number, error: Error) => {
      if (error instanceof ApiClientError && error.status === 401) {
        return false;
      }
      return failureCount < 3;
    },
    // Refetch on mount if data is stale (e.g., after invalidation from other pages)
    // This ensures data added via AI check-in appears when navigating to timeline
    refetchOnMount: true,
  });

  return {
    /** Array of pages containing daily records */
    pages: query.data?.pages ?? null,
    /** All daily records flattened from all pages */
    records: (query.data?.pages.flatMap((page) => page.data) ?? []) as DailyRecordResponse[],
    /** Whether the initial data is currently being fetched */
    isLoading: query.isLoading,
    /** Whether the fetch failed with an error */
    isError: query.isError,
    /** Error details if the fetch failed */
    error: query.error,
    /** Whether there is a next page available */
    hasNextPage: query.hasNextPage,
    /** Whether the next page is currently being fetched */
    isFetchingNextPage: query.isFetchingNextPage,
    /** Function to fetch the next page */
    fetchNextPage: query.fetchNextPage,
    /** Refetch all pages manually */
    refetch: query.refetch,
  };
}
